import { useRef, useState } from "react";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import {
  useRecognizeVaDevicePhotoMutation,
} from "../../../app/orderApi/OrderApi";
import { showToast } from "../../../helpers/Utils";
import { UploadSvg } from "../../../svgFiles/UploadSvg";

const MAX_PHOTO_SIZE = 10 * 1024 * 1024;

function formatMac(value) {
  const hex = String(value || "")
    .toUpperCase()
    .replace(/[^0-9A-F]/g, "")
    .slice(0, 12);
  return hex.match(/.{1,2}/g)?.join(":") || "";
}

function formatSerialNumber(value) {
  return String(value || "").toUpperCase().replace(/\s+/g, "").slice(0, 64);
}

function getErrorMessage(error, fallback) {
  return error?.data?.message || error?.error || error?.message || fallback;
}

export default function CreateVaDeviceOrderModal({ onClose, onContinue }) {
  const inputRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [macAddress, setMacAddress] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [recognitionMessage, setRecognitionMessage] = useState("");
  const [recognizePhoto, { isLoading: isRecognizing }] =
    useRecognizeVaDevicePhotoMutation();

  const busy = isRecognizing;
  const canCreate =
    /^[0-9A-F]{2}(?::[0-9A-F]{2}){5}$/.test(macAddress) &&
    Boolean(serialNumber) &&
    !busy;

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setPhoto(null);
      setPhotoError("Only image files are allowed.");
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setPhoto(null);
      setPhotoError("Photo exceeds the 10 MB limit.");
      return;
    }

    setPhoto(file);
    setPhotoError("");
    setRecognitionMessage("Recognizing MAC and SN…");
    try {
      const response = await recognizePhoto({ photo: file }).unwrap();
      const result = response?.data || {};
      setMacAddress(formatMac(result.macAddress));
      setSerialNumber(formatSerialNumber(result.serialNumber));
      if (result.macAddress && result.serialNumber) {
        setRecognitionMessage("MAC and SN recognized. Please verify or edit them.");
      } else {
        setRecognitionMessage("Some information was not recognized. Please enter it manually.");
      }
    } catch (error) {
      setRecognitionMessage("Recognition failed. You can enter MAC and SN manually.");
      showToast(getErrorMessage(error, "Unable to recognize this photo."), "error");
    }
  };

  const handleContinue = () => {
    if (!canCreate) return;
    onContinue?.({ photo, macAddress, serialNumber });
  };

  return (
    <CommonModal
      show
      setShow={(open) => {
        if (!open) onClose?.();
      }}
      title="Create VA Device Order"
      className="modal-sm va-device-order-modal"
      centered
      closeOnBackdrop={false}
      onConfirm={handleContinue}
      onCancel={onClose}
      confirmLabel="Next"
      confirmDisabled={!canCreate}
      cancelDisabled={busy}
    >
      <div className="va-device-order-form">
        <div>
          <label className="va-device-order-form__label" htmlFor="va-device-photo">
            Frame photo
          </label>
          <button
            type="button"
            className="va-device-order-form__photo-button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            <span aria-hidden="true"><UploadSvg /></span>
            <span>{busy ? "Recognizing photo…" : photo ? "Choose another photo" : "Take or choose frame photo"}</span>
          </button>
          <input
            ref={inputRef}
            id="va-device-photo"
            type="file"
            accept="image/*"
            className="d-none"
            onChange={handlePhotoChange}
          />
          {photo && <p className="va-device-order-form__file-name">{photo.name}</p>}
          {photoError && <p className="va-device-order-form__error">{photoError}</p>}
          {recognitionMessage && (
            <p className="va-device-order-form__hint">{recognitionMessage}</p>
          )}
        </div>

        <label className="va-device-order-form__field">
          <span>MAC *</span>
          <input
            type="text"
            className="form-control"
            value={macAddress}
            placeholder="AA:BB:CC:DD:EE:FF"
            onChange={(event) => setMacAddress(formatMac(event.target.value))}
            disabled={busy}
          />
        </label>

        <label className="va-device-order-form__field">
          <span>SN *</span>
          <input
            type="text"
            className="form-control"
            value={serialNumber}
            placeholder="Enter serial number"
            onChange={(event) =>
              setSerialNumber(formatSerialNumber(event.target.value))
            }
            disabled={busy}
          />
        </label>
      </div>
    </CommonModal>
  );
}
